import { UserService } from "../services/UserService";

/**
 * Maps user groups to a persona name
 */
export function mapPersonaId(groups: string[]): string {
  if (groups.includes('SFE Owner')) return 'SFE Owner';
  if (groups.includes('SFE RSM ISR')) return 'SFE RSM ISR';
  if (groups.includes('SFE RVP')) return 'SFE RVP';
  if (groups.includes('SFE RDD')) return 'SFE RDD';
  if (groups.includes('SFE Marketing')) return 'SFE Marketing';
  if (groups.includes('SFE BDT')) return 'SFE BDT';
  if (groups.includes('SFE Visitor')) return 'SFE Visitor';
  return 'SFE Visitor'; // Default
}

/**
 * Maps user groups to an access level
 */
export function mapAccessLevelId(groups: string[]): string {
  if (groups.includes('SFE Administrator')) return 'SFE Administrator';
  if (groups.includes('SFE Superuser')) return 'SFE Superuser';
  if (groups.includes('SFE Advanced User')) return 'SFE Advanced User';
  if (groups.includes('SFE Standard User')) return 'SFE Standard User';
  return 'SFE Reviewer'; // Default
}

/**
 * Checks if user data needs to be updated from Okta
 */
export function shouldUpdateUserFromOkta(backendUser: any, oktaUserInfo: any): boolean {
  // Check if any critical fields have changed
  if (backendUser.email !== oktaUserInfo.email) return true;
  if (backendUser.given_name !== oktaUserInfo.given_name) return true;
  if (backendUser.family_name !== oktaUserInfo.family_name) return true;
  if (backendUser.locale !== oktaUserInfo.locale) return true;
  if (backendUser.zoneinfo !== oktaUserInfo.zoneinfo) return true;

  // Check if the last update was more than 24 hours ago
  const userData = UserService.getUserData();
  if (userData) {
    const lastUpdateAttemptTime = userData.updateAttemptedAt;
    const now = Date.now();
    const twentyFourHours = 24 * 60 * 60 * 1000;

    if (now - lastUpdateAttemptTime > twentyFourHours) {
      return true;
    }
  } else {
    // If no user data is found, update
    return true;
  }

  return false;
}

/**
 * Checks if a user belongs to a specific group
 */
export function hasGroup(userGroups: string[], groupName: string): boolean {
  if (!userGroups || userGroups.length === 0) return false;
  
  // Convert search term to lowercase and remove special characters
  const normalizedSearch = groupName.toLowerCase().replace(/[^a-z0-9]/g, '');
  
  return userGroups.some(g => {
    // Normalize group name for comparison
    const normalizedGroup = g.toLowerCase().replace(/[^a-z0-9]/g, '');
    return normalizedGroup.includes(normalizedSearch) || 
           normalizedSearch.includes(normalizedGroup);
  });
}

/**
 * Maps group membership to display names
 */
export function getPersonaDisplayName(userGroups: string[]): string {
  if (hasGroup(userGroups, 'SFE Owner')) return 'Owner';
  if (hasGroup(userGroups, 'SFE BDT')) return 'BDT';
  if (hasGroup(userGroups, 'SFE RSM ISR')) return 'RSM';
  if (hasGroup(userGroups, 'SFE RVP')) return 'RVP';
  if (hasGroup(userGroups, 'SFE RD&D')) return 'RD&D';
  if (hasGroup(userGroups, 'SFE Marketing')) return 'Marketing';
  return 'Visitor';
}

/**
 * Maps access level to display names
 */
export function getAccessLevelDisplayName(userGroups: string[]): string {
  if (hasGroup(userGroups, 'SFE Administrator')) return 'Administrator';
  if (hasGroup(userGroups, 'SFE Superuser')) return 'Superuser';
  if (hasGroup(userGroups, 'SFE Advanced User')) return 'Advanced User';
  if (hasGroup(userGroups, 'SFE Standard User')) return 'Standard User';
  return 'Reviewer';
}
